import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Upload, Trash2, Check, AlertTriangle, Loader2, ArrowLeft, ArrowRight, User, Users, UsersRound, Plus, Copy, Baby } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, apiError, money } from "../lib/api";
import { toast } from "sonner";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const MAX_PHOTO_MB = 5;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const DRAFT_KEY = "hz_registro_draft";

const REG_TYPES = [
  {
    id: "individual", icon: User, title: "Registro individual",
    text: "Para registrar únicamente a una persona adulta. Puedes agregar niños acompañantes si utilizarán el área de cuidado infantil.",
  },
  {
    id: "family", icon: Users, title: "Registro familiar",
    text: "Para registrar a varios integrantes de una misma familia. Cada adulto tendrá su acceso y QR individual. Los niños podrán vincularse al cuidado infantil.",
  },
  {
    id: "group", icon: UsersRound, title: "Registro grupal",
    text: "Para registrar a varias personas adultas bajo un mismo contacto. Cada persona tendrá su acceso y QR individual.",
  },
];

const newAdult = () => ({
  full_name: "", email: "", phone: "", use_contact_data: true,
  delegation_id: "", sector_id: "", general_event: false, dinner: false, workshops: [],
  photoFile: null, photoPreview: null,
});

const newChild = () => ({
  full_name: "", photoFile: null, photoPreview: null, birth_date: "", age: "",
  allergies: "", medical_notes: "", special_needs: "", care_required: true,
  authorized: [], extName: "", extPhone: "", extRelationship: "",
});

const FieldError = ({ msg, id }) =>
  msg ? (
    <p className="mt-1 text-xs font-medium text-hz-error" data-testid={`${id}-error`} role="alert">{msg}</p>
  ) : null;

const PhotoPicker = ({ preview, onPick, onRemove, testId }) => {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  return (
    <div>
      <input ref={cameraRef} type="file" accept="image/jpeg,image/png,image/webp" capture="user" className="hidden" data-testid={`${testId}-camera-input`} onChange={(e) => onPick(e.target.files?.[0])} />
      <input ref={galleryRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={`${testId}-upload-input`} onChange={(e) => onPick(e.target.files?.[0])} />
      {preview ? (
        <div className="mt-2 flex items-center gap-4">
          <img src={preview} alt="Fotografía" className="h-20 w-20 rounded-2xl border border-hz-gray/50 object-cover" data-testid={`${testId}-preview`} />
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" data-testid={`${testId}-replace-btn`} onClick={() => galleryRef.current?.click()} className="font-semibold">Reemplazar</Button>
            <Button type="button" variant="outline" size="sm" data-testid={`${testId}-remove-btn`} onClick={onRemove} className="border-hz-error/40 font-semibold text-hz-error hover:bg-hz-error/5">
              <Trash2 className="mr-1 h-4 w-4" /> Eliminar
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-2 grid grid-cols-2 gap-3">
          <button type="button" data-testid={`${testId}-camera-btn`} onClick={() => cameraRef.current?.click()} className="flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-hz-gray/60 px-4 py-5 text-sm font-semibold text-hz-navy transition-colors hover:border-hz-cyan hover:text-hz-cyan">
            <Camera className="h-5 w-5" /> Tomar foto
          </button>
          <button type="button" data-testid={`${testId}-gallery-btn`} onClick={() => galleryRef.current?.click()} className="flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-hz-gray/60 px-4 py-5 text-sm font-semibold text-hz-navy transition-colors hover:border-hz-cyan hover:text-hz-cyan">
            <Upload className="h-5 w-5" /> Subir imagen
          </button>
        </div>
      )}
      <p className="mt-1.5 text-xs text-hz-ink/50" data-testid={`${testId}-camera-hint`}>En algunos dispositivos, la cámara puede abrirse mediante el selector del sistema.</p>
    </div>
  );
};

const RegistroPage = () => {
  const navigate = useNavigate();
  const [regType, setRegType] = useState(null);
  const [step, setStep] = useState(1);
  const [catalog, setCatalog] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [pricing, setPricing] = useState({});
  const [dinnerAvail, setDinnerAvail] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [duplicateWarn, setDuplicateWarn] = useState(null);

  const [contact, setContact] = useState({ name: "", email: "", phone: "", groupName: "" });
  const [adults, setAdults] = useState([]);
  const [children, setChildren] = useState([]);
  const [privacy, setPrivacy] = useState(false);
  const [minorsAuth, setMinorsAuth] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    Promise.all([api.get("/public/catalog"), api.get("/public/workshops"), api.get("/public/pricing")])
      .then(([c, w, p]) => {
        setCatalog(c.data.delegations || []);
        setWorkshops(w.data.workshops || []);
        const map = {};
        (p.data.pricing || []).forEach((i) => (map[i.code] = i));
        setPricing(map);
        setDinnerAvail(p.data.dinner_availability || null);
      })
      .catch(() => setSubmitError("No pudimos cargar la información del evento. Recarga la página."))
      .finally(() => setLoadingData(false));
    try {
      const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (draft && draft.regType) {
        setRegType(draft.regType);
        setContact(draft.contact || { name: "", email: "", phone: "", groupName: "" });
        setAdults((draft.adults || []).map((a) => ({ ...a, photoFile: null, photoPreview: null })));
        setChildren((draft.children || []).map((c) => ({ ...c, photoFile: null, photoPreview: null })));
        setStep(draft.step || 1);
      }
    } catch { /* sin borrador */ }
  }, []);

  const saveDraft = (nextStep) => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        regType, contact, step: nextStep,
        adults: adults.map(({ photoFile, photoPreview, ...a }) => a),
        children: children.map(({ photoFile, photoPreview, ...c }) => c),
      }));
    } catch { /* almacenamiento lleno */ }
  };

  const stepTitles = useMemo(() => {
    if (!regType) return [];
    if (regType === "group") return ["Contacto", "Adultos", "Resumen"];
    return ["Contacto", regType === "family" ? "Adultos" : "Adulto", "Niños (cuidado infantil)", "Resumen"];
  }, [regType]);
  const maxStep = stepTitles.length;

  const updateAdult = (i, patch) => setAdults((list) => list.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  const updateChild = (i, patch) => setChildren((list) => list.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  const pickPhoto = (setter, index, file) => {
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      setErrors((e) => ({ ...e, [`photo_${setter}_${index}`]: "Formato no permitido. Usa JPG, PNG o WEBP." }));
      return;
    }
    if (file.size > MAX_PHOTO_MB * 1024 * 1024) {
      setErrors((e) => ({ ...e, [`photo_${setter}_${index}`]: `La fotografía excede ${MAX_PHOTO_MB} MB.` }));
      return;
    }
    const preview = URL.createObjectURL(file);
    if (setter === "adult") updateAdult(index, { photoFile: file, photoPreview: preview });
    else updateChild(index, { photoFile: file, photoPreview: preview });
    setErrors((e) => ({ ...e, [`photo_${setter}_${index}`]: undefined }));
  };

  const removeAdult = (i) => {
    if (!window.confirm(`¿Eliminar a ${adults[i].full_name || `el adulto ${i + 1}`} del registro?`)) return;
    setAdults((list) => list.filter((_, idx) => idx !== i));
    setChildren((list) => list.map((c) => ({ ...c, authorized: c.authorized.filter((x) => x !== i).map((x) => (x > i ? x - 1 : x)) })));
  };
  const removeChild = (i) => {
    if (!window.confirm(`¿Eliminar a ${children[i].full_name || `el niño ${i + 1}`} del registro?`)) return;
    setChildren((list) => list.filter((_, idx) => idx !== i));
  };

  const sectorsOf = (delegationId) => catalog.find((d) => d.id === delegationId)?.sectors || [];

  const validateStep = (n) => {
    const e = {};
    if (n === 1) {
      if (contact.name.trim().length < 3) e.contact_name = "Ingresa el nombre del contacto principal.";
      if (!contact.email.trim() && !contact.phone.trim()) e.contact_email = "Ingresa al menos un correo o un teléfono de contacto.";
      if (contact.email.trim() && !EMAIL_RE.test(contact.email.trim())) e.contact_email = "Correo electrónico no válido.";
      const digits = contact.phone.replace(/\D/g, "");
      if (contact.phone.trim() && (digits.length < 8 || digits.length > 15)) e.contact_phone = "Teléfono no válido.";
    }
    if (n === 2) {
      adults.forEach((a, i) => {
        const name = regType === "individual" ? contact.name : a.full_name;
        if (name.trim().length < 3) e[`adult_name_${i}`] = "Ingresa el nombre completo.";
        if (!a.photoFile) e[`photo_adult_${i}`] = "La fotografía es obligatoria.";
        if (!a.use_contact_data && regType !== "individual") {
          if (a.email.trim() && !EMAIL_RE.test(a.email.trim())) e[`adult_email_${i}`] = "Correo no válido.";
          const dg = a.phone.replace(/\D/g, "");
          if (a.phone.trim() && (dg.length < 8 || dg.length > 15)) e[`adult_phone_${i}`] = "Teléfono no válido.";
        }
        if (!a.delegation_id || !a.sector_id) e[`adult_org_${i}`] = "Selecciona delegación y sector.";
      });
    }
    if (n === 3 && regType !== "group") {
      children.forEach((c, i) => {
        if (c.full_name.trim().length < 3) e[`child_name_${i}`] = "Ingresa el nombre completo del menor.";
        // La fotografía del menor puede completarse después; el cargo de cuidado infantil se mantiene.
        const extOk = c.extName.trim().length >= 3 && c.extPhone.replace(/\D/g, "").length >= 8 && c.extRelationship.trim();
        if (c.authorized.length === 0 && !extOk) e[`child_auth_${i}`] = "Selecciona al menos un adulto autorizado para recogerlo.";
      });
    }
    if (n === maxStep) {
      if (!privacy) e.privacy = "Debes confirmar tus datos y aceptar el aviso de privacidad.";
      if (children.length > 0 && !minorsAuth) e.minors = "Debes confirmar que cuentas con autorización de los padres o tutores.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validateStep(step)) return;
    saveDraft(step + 1);
    setStep((s) => Math.min(maxStep, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(false);
  const [quoteKeyUsed, setQuoteKeyUsed] = useState(null);
  const [quoteRetry, setQuoteRetry] = useState(0);
  const quoteKey = JSON.stringify({
    t: regType,
    a: adults.map((a) => [a.general_event, a.dinner, a.workshops]),
    c: children.map((c) => c.care_required),
  });
  useEffect(() => {
    const timer = setTimeout(async () => {
      setQuoteLoading(true);
      setQuoteError(false);
      try {
        const { data } = await api.post("/public/registration-quote", {
          group_type: regType,
          adults: adults.map((a) => ({ general_event: a.general_event, dinner: a.dinner, workshop_ids: a.workshops })),
          care_children_count: children.filter((c) => c.care_required).length,
        });
        setQuote(data);
      } catch {
        setQuoteError(true);
        setQuote(null);
      } finally {
        setQuoteKeyUsed(quoteKey);
        setQuoteLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteKey, quoteRetry]);

  // La cotización solo es vigente si corresponde exactamente a las selecciones actuales.
  const quoteFresh = !quoteLoading && quoteKeyUsed === quoteKey;
  const quoteValid = quoteFresh && !!quote && quote.valid === true && quote.total_amount != null;
  const quoteFailed = quoteFresh && (quoteError || (!!quote && quote.valid === false && (quote.errors || []).length > 0));

  const [swapPrompt, setSwapPrompt] = useState(null);

  const wsOverlap = (w1, w2) => {
    if (!w1?.starts_at || !w1?.ends_at || !w2?.starts_at || !w2?.ends_at) return false;
    return new Date(w1.starts_at) < new Date(w2.ends_at) && new Date(w2.starts_at) < new Date(w1.ends_at);
  };
  const wsConflictWith = (a, wid) => {
    const w = workshops.find((x) => x.id === wid);
    if (!w) return null;
    for (const selId of a.workshops) {
      const s = workshops.find((x) => x.id === selId);
      if (s && wsOverlap(w, s)) return s;
    }
    return null;
  };
  const fmtWsTime = (w) => (w?.starts_at && w?.ends_at)
    ? `${String(w.starts_at).slice(11, 16)} a ${String(w.ends_at).slice(11, 16)} h`
    : "horario por confirmar";

  const toggleGeneralEvent = (i) => {
    const a = adults[i];
    if (a.general_event && a.workshops.length > 0) {
      toast.error("Los talleres seleccionados requieren acceso al evento general del sábado. Quita primero los talleres si deseas deseleccionarlo.");
      return;
    }
    updateAdult(i, { general_event: !a.general_event });
  };
  const dinnerUnavailable = !!(dinnerAvail && (!dinnerAvail.enabled || dinnerAvail.closed || (dinnerAvail.state === "full" && !dinnerAvail.waitlist_enabled)));
  const dinnerWaitlistMode = !!(dinnerAvail && dinnerAvail.enabled && !dinnerAvail.closed && dinnerAvail.state === "full" && dinnerAvail.waitlist_enabled);
  const toggleDinner = (i) => {
    const a = adults[i];
    if (!a.dinner && dinnerUnavailable) {
      toast.error(dinnerAvail?.full_message || "La cena especial ya no tiene lugares disponibles.");
      return;
    }
    if (!a.dinner && dinnerWaitlistMode) {
      toast.info(dinnerAvail?.waitlist_message || "Tu solicitud quedará en lista de espera.");
    }
    // La cena es independiente del evento general del sábado: no auto-seleccionar nada.
    updateAdult(i, { dinner: !a.dinner });
  };
  const toggleWorkshop = (i, wid) => {
    const a = adults[i];
    const selected = a.workshops.includes(wid);
    if (selected) {
      updateAdult(i, { workshops: a.workshops.filter((x) => x !== wid) });
      setSwapPrompt(null);
      return;
    }
    const conflict = wsConflictWith(a, wid);
    if (conflict) {
      const w = workshops.find((x) => x.id === wid);
      toast.error(`No puedes seleccionar este taller porque coincide con "${conflict.name}", programado de ${fmtWsTime(conflict)}.`);
      setSwapPrompt({ adultIdx: i, newId: wid, conflictId: conflict.id, newName: w?.name, conflictName: conflict.name, conflictTime: fmtWsTime(conflict) });
      return;
    }
    setSwapPrompt(null);
    if (!a.general_event) {
      updateAdult(i, { workshops: [...a.workshops, wid], general_event: true });
      toast.info("Los talleres seleccionados requieren acceso al evento general del sábado. Se agregó automáticamente.");
    } else {
      updateAdult(i, { workshops: [...a.workshops, wid] });
    }
  };

  const resolveSwap = (replace) => {
    if (!swapPrompt) return;
    if (replace) {
      const a = adults[swapPrompt.adultIdx];
      updateAdult(swapPrompt.adultIdx, { workshops: [...a.workshops.filter((x) => x !== swapPrompt.conflictId), swapPrompt.newId], general_event: true });
      toast.info(`Se reemplazó "${swapPrompt.conflictName}" por "${swapPrompt.newName}".`);
    }
    setSwapPrompt(null);
  };

  const uploadPhoto = async (file) => {
    if (!file) return null;
    const fd = new FormData();
    fd.append("photo", file);
    const { data } = await api.post("/registrations/photos", fd);
    return data.photo_path;
  };

  const submit = async (confirmDuplicates = false) => {
    if (!validateStep(maxStep) || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    setDuplicateWarn(null);
    try {
      const adultsPayload = [];
      for (let i = 0; i < adults.length; i++) {
        const a = adults[i];
        adultsPayload.push({
          full_name: regType === "individual" ? contact.name.trim() : a.full_name.trim(),
          email: regType === "individual" || a.use_contact_data ? null : a.email.trim() || null,
          phone: regType === "individual" || a.use_contact_data ? null : a.phone || null,
          use_contact_data: regType === "individual" || a.use_contact_data,
          delegation_id: a.delegation_id, sector_id: a.sector_id,
          // Selección explícita del usuario (sin acoples): la cena NO fuerza evento general.
          // El backend derivará evento general como dependencia operativa SOLO si hay talleres.
          general_event: a.general_event || a.workshops.length > 0,
          dinner: a.dinner, workshop_ids: a.workshops,
          // Códigos elegidos explícitamente por el usuario (contrato explícito para evitar cargos no seleccionados).
          selected_codes: [
            ...(a.general_event ? ["general_event"] : []),
            ...(a.dinner ? ["dinner"] : []),
            ...a.workshops.map((wid) => `workshop:${wid}`),
          ],
          photo_path: await uploadPhoto(a.photoFile),
        });
      }
      const childrenPayload = [];
      for (let i = 0; i < children.length; i++) {
        const c = children[i];
        const extOk = c.extName.trim().length >= 3 && c.extPhone.replace(/\D/g, "").length >= 8 && c.extRelationship.trim();
        childrenPayload.push({
          full_name: c.full_name.trim(),
          photo_path: await uploadPhoto(c.photoFile),
          birth_date: c.birth_date || null,
          age: c.age ? parseInt(c.age, 10) : null,
          allergies: c.allergies || null, medical_notes: c.medical_notes || null,
          special_needs: c.special_needs || null, care_required: c.care_required,
          authorized_adult_indices: c.authorized,
          external_guardians: extOk ? [{ name: c.extName.trim(), phone: c.extPhone, relationship: c.extRelationship.trim() }] : [],
        });
      }
      const payload = {
        group_type: regType,
        group_name: contact.groupName.trim() || null,
        primary_contact_name: contact.name.trim(),
        primary_contact_email: contact.email.trim() || null,
        primary_contact_phone: contact.phone || null,
        adults: adultsPayload,
        children: childrenPayload,
        privacy_accepted: privacy,
        minors_authorized: minorsAuth,
        confirm_duplicates: confirmDuplicates,
      };
      const { data } = await api.post("/registrations/group", payload);
      localStorage.removeItem(DRAFT_KEY);
      try {
        sessionStorage.setItem("hz_last_reg", JSON.stringify({
          folio: data.group_folio,
          email: contact.email.trim() || null,
          phone: contact.phone || null,
        }));
      } catch { /* sin sesión */ }
      navigate(`/registro/confirmacion/${data.group_folio}`);
    } catch (err) {
      const errCode = err?.response?.data?.detail?.code;
      if (err?.response?.status === 409 && errCode === "DINNER_SOLD_OUT") {
        setAdults((prev) => prev.map((a) => ({ ...a, dinner: false })));
        api.get("/public/pricing").then((p) => setDinnerAvail(p.data.dinner_availability || null)).catch(() => {});
        setSubmitError("La cena especial se agotó mientras completabas tu registro. Ya la retiramos de tu selección; puedes continuar sin incluirla.");
        toast.error("La cena especial se agotó mientras completabas tu registro. Puedes continuar sin incluirla.");
      } else if (err?.response?.status === 409) setDuplicateWarn(apiError(err, "Posible duplicado detectado."));
      else setSubmitError(apiError(err, "No se pudo enviar tu pre-registro. Intenta de nuevo."));
    } finally {
      setSubmitting(false);
    }
  };

  if (!regType) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14">
          <p className="text-xs font-semibold uppercase tracking-widest text-hz-blue">Pre-registro</p>
          <h1 className="mt-2 text-2xl font-bold tracking-tight text-hz-navy sm:text-3xl" data-testid="registro-title">Regístrate a Horizonte 2026</h1>
          <p className="mt-2 text-sm text-hz-ink/70">Elige el tipo de registro que mejor se adapte a tu situación.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-3" data-testid="reg-type-cards">
            {REG_TYPES.map((t) => (
              <button key={t.id} type="button" data-testid={`reg-type-${t.id}`} onClick={() => { setRegType(t.id); setAdults([newAdult()]); setChildren([]); setStep(1); }}
                className="flex flex-col rounded-2xl border-2 border-hz-gray/40 bg-white p-6 text-left transition-all hover:-translate-y-0.5 hover:border-hz-cyan hover:shadow-md">
                <t.icon className="h-8 w-8 text-hz-cyan" />
                <p className="mt-3 font-bold text-hz-navy">{t.title}</p>
                <p className="mt-2 text-xs leading-relaxed text-hz-ink/70">{t.text}</p>
              </button>
            ))}
          </div>
          <p className="mt-6 rounded-xl border border-hz-cyan/30 bg-hz-cyan/5 px-4 py-3 text-sm font-medium text-hz-ink" data-testid="reg-type-note">
            Puedes usar el mismo correo electrónico o teléfono para varios integrantes. Cada adulto obtiene un folio y QR individual después de confirmar el pago.
          </p>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14">
        <p className="text-xs font-semibold uppercase tracking-widest text-hz-blue">
          {REG_TYPES.find((t) => t.id === regType)?.title}
        </p>
        <h1 className="mt-2 text-2xl font-bold tracking-tight text-hz-navy sm:text-3xl" data-testid="registro-title">Regístrate a Horizonte 2026</h1>

        <div className="mt-8" data-testid="progress-bar">
          <div className="flex gap-2">
            {stepTitles.map((t, i) => (
              <div key={t} className="flex-1">
                <div className={`h-1.5 rounded-full transition-colors ${step > i ? "bg-hz-cyan" : "bg-hz-gray/50"}`} />
                <p className={`mt-2 text-[11px] font-semibold sm:text-xs ${step > i ? "text-hz-navy" : "text-hz-ink/40"}`}>{i + 1}. {t}</p>
              </div>
            ))}
          </div>
        </div>

        {loadingData ? (
          <div className="mt-16 flex items-center justify-center gap-2 text-hz-navy" data-testid="registro-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Cargando información del evento…
          </div>
        ) : (
          <div className="mt-8 rounded-2xl border border-hz-gray/40 bg-white p-6 shadow-sm sm:p-8">
            {/* PASO 1: contacto */}
            {step === 1 && (
              <div className="space-y-5" data-testid="step-contact">
                <h2 className="text-lg font-bold text-hz-navy">
                  {regType === "individual" ? "Tus datos de contacto" : "Datos del responsable del registro"}
                </h2>
                {(regType === "family" || regType === "group") && (
                  <div>
                    <Label htmlFor="group_name" className="font-semibold text-hz-ink">
                      {regType === "family" ? "Nombre de la familia (opcional)" : "Nombre del grupo *"}
                    </Label>
                    <Input id="group_name" data-testid="input-group-name" value={contact.groupName} onChange={(e) => setContact({ ...contact, groupName: e.target.value })} placeholder={regType === "family" ? "Ej. Familia Pérez" : "Ej. Iglesia Vida Nueva"} className="mt-1.5" />
                  </div>
                )}
                <div>
                  <Label htmlFor="contact_name" className="font-semibold text-hz-ink">
                    {regType === "individual" ? "Nombre completo *" : "Nombre completo del contacto principal *"}
                  </Label>
                  <Input id="contact_name" data-testid="input-full-name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Ej. María Pérez López" className="mt-1.5" autoComplete="name" />
                  <FieldError msg={errors.contact_name} id="contact-name" />
                </div>
                <div>
                  <Label htmlFor="contact_email" className="font-semibold text-hz-ink">Correo electrónico {regType === "individual" ? "*" : ""}</Label>
                  <Input id="contact_email" type="email" data-testid="input-email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="tucorreo@ejemplo.com" className="mt-1.5" autoComplete="email" />
                  <FieldError msg={errors.contact_email} id="contact-email" />
                </div>
                <div>
                  <Label htmlFor="contact_phone" className="font-semibold text-hz-ink">Teléfono {regType === "individual" ? "*" : ""}</Label>
                  <Input id="contact_phone" type="tel" data-testid="input-phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="Ej. 55 1234 5678" className="mt-1.5" autoComplete="tel" />
                  <FieldError msg={errors.contact_phone} id="contact-phone" />
                </div>
                {regType !== "individual" && (
                  <p className="rounded-lg bg-hz-bg px-3 py-2 text-xs font-medium text-hz-ink/70">
                    Los integrantes podrán usar este correo y teléfono como contacto compartido.
                  </p>
                )}
                {regType !== "individual" && (
                  <p className="rounded-lg border border-hz-cyan/30 bg-hz-cyan/5 px-3 py-2 text-xs font-medium text-hz-ink" data-testid="single-payment-notice">
                    El pago se realiza mediante un solo comprobante por el total del grupo. Si algún integrante pagará por separado, debe registrarse en otro grupo.
                  </p>
                )}
              </div>
            )}

            {/* PASO 2: adultos */}
            {step === 2 && (
              <div className="space-y-6" data-testid="step-adults">
                <h2 className="text-lg font-bold text-hz-navy">{regType === "individual" ? "Tu fotografía, ubicación y accesos" : "Adultos del registro"}</h2>
                {adults.map((a, i) => (
                  <div key={i} className="space-y-5 rounded-xl border border-hz-gray/40 bg-hz-bg/50 p-4" data-testid={`adult-card-${i}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-bold text-hz-navy">{regType === "individual" ? "Adulto" : `Adulto ${i + 1}`}{i === 0 && regType !== "individual" ? " · contacto principal" : ""}</p>
                      {adults.length > 1 && (
                        <button type="button" onClick={() => removeAdult(i)} data-testid={`adult-remove-${i}`} className="text-xs font-bold text-hz-error hover:underline">
                          <Trash2 className="mr-1 inline h-3.5 w-3.5" /> Eliminar
                        </button>
                      )}
                    </div>
                    {regType !== "individual" && (
                      <div>
                        <Label className="font-semibold text-hz-ink">Nombre completo *</Label>
                        <Input data-testid={`adult-name-${i}`} value={a.full_name} onChange={(e) => updateAdult(i, { full_name: e.target.value })} placeholder="Ej. Juan Pérez López" className="mt-1.5" />
                        <FieldError msg={errors[`adult_name_${i}`]} id={`adult-name-${i}`} />
                      </div>
                    )}
                    <div>
                      <Label className="font-semibold text-hz-ink">Fotografía *</Label>
                      <PhotoPicker preview={a.photoPreview} testId={`adult-photo-${i}`}
                        onPick={(f) => pickPhoto("adult", i, f)}
                        onRemove={() => updateAdult(i, { photoFile: null, photoPreview: null })} />
                      <FieldError msg={errors[`photo_adult_${i}`]} id={`adult-photo-${i}`} />
                    </div>
                    {regType !== "individual" && (
                      <div className="space-y-3">
                        <label className="flex items-center gap-2 text-sm font-medium text-hz-ink">
                          <Checkbox data-testid={`adult-use-contact-${i}`} checked={a.use_contact_data} onCheckedChange={(v) => updateAdult(i, { use_contact_data: v === true })} />
                          Usar datos de contacto del responsable
                        </label>
                        {!a.use_contact_data && (
                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <Label className="text-xs font-semibold text-hz-ink">Correo (opcional)</Label>
                              <Input type="email" data-testid={`adult-email-${i}`} value={a.email} onChange={(e) => updateAdult(i, { email: e.target.value })} className="mt-1" />
                              <FieldError msg={errors[`adult_email_${i}`]} id={`adult-email-${i}`} />
                            </div>
                            <div>
                              <Label className="text-xs font-semibold text-hz-ink">Teléfono (opcional)</Label>
                              <Input type="tel" data-testid={`adult-phone-${i}`} value={a.phone} onChange={(e) => updateAdult(i, { phone: e.target.value })} className="mt-1" />
                              <FieldError msg={errors[`adult_phone_${i}`]} id={`adult-phone-${i}`} />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <Label className="text-xs font-semibold text-hz-ink">Delegación *</Label>
                        <Select value={a.delegation_id} onValueChange={(v) => updateAdult(i, { delegation_id: v, sector_id: "" })}>
                          <SelectTrigger data-testid={`adult-delegation-${i}`} className="mt-1"><SelectValue placeholder="Delegación" /></SelectTrigger>
                          <SelectContent>{catalog.map((d) => <SelectItem key={d.id} value={d.id} data-testid={`adult-delegation-option-${i}-${d.id}`}>{d.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-hz-ink">Sector *</Label>
                        <Select value={a.sector_id} disabled={!a.delegation_id} onValueChange={(v) => updateAdult(i, { sector_id: v })}>
                          <SelectTrigger data-testid={`adult-sector-${i}`} className="mt-1"><SelectValue placeholder="Sector" /></SelectTrigger>
                          <SelectContent>{sectorsOf(a.delegation_id).map((s) => <SelectItem key={s.id} value={s.id} data-testid={`adult-sector-option-${i}-${s.id}`}>{s.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <FieldError msg={errors[`adult_org_${i}`]} id={`adult-org-${i}`} />
                    {i > 0 && (
                      <button type="button" data-testid={`adult-copy-org-${i}`}
                        onClick={() => updateAdult(i, { delegation_id: adults[i - 1].delegation_id, sector_id: adults[i - 1].sector_id })}
                        className="flex items-center gap-1 text-xs font-bold text-hz-cyan hover:underline">
                        <Copy className="h-3.5 w-3.5" /> Copiar organización del adulto anterior
                      </button>
                    )}
                    <div className="space-y-3 border-t border-hz-gray/30 pt-4">
                      <button type="button" data-testid={`adult-general-event-${i}`} onClick={() => toggleGeneralEvent(i)} aria-pressed={a.general_event}
                        className={`flex w-full items-start justify-between gap-4 rounded-xl border-2 p-3 text-left transition-colors ${a.general_event ? "border-hz-cyan bg-hz-cyan/5" : "border-hz-gray/50 bg-white hover:border-hz-cyan/50"}`}>
                        <div>
                          <p className="text-sm font-bold text-hz-ink">{pricing.general_event?.name || "Evento general — sábado"}</p>
                          {pricing.general_event?.description && <p className="mt-0.5 text-xs text-hz-ink/60">{pricing.general_event.description}</p>}
                          {pricing.general_event?.schedule && <p className="mt-0.5 text-xs font-semibold text-hz-blue">{pricing.general_event.schedule}</p>}
                          {pricing.general_event?.includes && <p className="mt-0.5 text-xs text-hz-ink/60">Incluye: {pricing.general_event.includes}</p>}
                          {a.workshops.length > 0 && a.general_event && (
                            <p className="mt-1 text-xs font-medium text-hz-blue" data-testid={`adult-general-required-note-${i}`}>Requerido por los talleres seleccionados.</p>
                          )}
                        </div>
                        <span className={`flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${a.general_event ? "bg-hz-cyan text-white" : "bg-hz-gray/30 text-hz-navy"}`}>
                          {a.general_event ? <><Check className="h-3.5 w-3.5" />Seleccionado</> : "Opcional"}
                        </span>
                      </button>
                      <button type="button" data-testid={`adult-dinner-${i}`} onClick={() => toggleDinner(i)} aria-pressed={a.dinner}
                        disabled={dinnerUnavailable && !a.dinner}
                        aria-disabled={dinnerUnavailable && !a.dinner ? "true" : undefined}
                        className={`flex w-full items-start justify-between gap-4 rounded-xl border-2 p-3 text-left transition-colors ${a.dinner ? "border-hz-cyan bg-hz-cyan/5" : dinnerUnavailable ? "cursor-not-allowed border-hz-gray/40 bg-hz-gray/10 opacity-60" : "border-hz-gray/50 bg-white hover:border-hz-cyan/50"}`}>
                        <div>
                          <p className="text-sm font-bold text-hz-ink">{pricing.dinner?.name || "Cena especial — viernes"}</p>
                          <p className="mt-0.5 text-xs text-hz-ink/60">Disponible solo con pago confirmado o cortesía.</p>
                          <p className="mt-0.5 text-xs font-semibold text-hz-ink/70" data-testid={`adult-dinner-independent-${i}`}>Disponible de forma independiente del evento general del sábado.</p>
                          {dinnerAvail?.enabled && !dinnerAvail?.closed && !dinnerUnavailable && dinnerAvail?.available != null && (
                            <p className={`mt-0.5 text-xs font-semibold ${dinnerAvail.low_inventory ? "text-hz-warning" : "text-hz-cyan"}`} data-testid={`adult-dinner-availability-${i}`}>
                              Cupo limitado · {dinnerAvail.low_inventory ? `Últimos ${dinnerAvail.available} lugares disponibles` : `${dinnerAvail.available} lugares disponibles`} · Tu lugar queda sujeto a pago dentro del tiempo de reserva.
                            </p>
                          )}
                          {dinnerUnavailable && <p className="mt-1 text-xs font-semibold text-hz-error" data-testid={`adult-dinner-full-msg-${i}`}>{dinnerAvail?.full_message}</p>}
                          {dinnerWaitlistMode && <p className="mt-1 text-xs font-semibold text-hz-warning" data-testid={`adult-dinner-waitlist-msg-${i}`}>{dinnerAvail?.waitlist_message}</p>}
                        </div>
                        <span className={`flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${a.dinner ? "bg-hz-cyan text-white" : "bg-hz-gray/30 text-hz-navy"}`}>
                          {a.dinner ? <><Check className="h-3.5 w-3.5" />Seleccionada</> : dinnerUnavailable ? "No disponible" : "Opcional"}
                        </span>
                      </button>
                      <div>
                        <p className="text-sm font-bold text-hz-ink">Talleres <span className="text-xs font-medium text-hz-ink/50">(opcionales)</span></p>
                        <div className="mt-2 space-y-2">
                          {workshops.filter((w) => w.schedule_complete !== false).map((w) => {
                            const selected = a.workshops.includes(w.id);
                            const full = w.available <= 0;
                            const conflict = !selected ? wsConflictWith(a, w.id) : null;
                            return (
                              <button key={w.id} type="button" data-testid={`adult-workshop-${i}-${w.id}`} disabled={full && !selected}
                                onClick={() => toggleWorkshop(i, w.id)}
                                className={`flex w-full items-start justify-between gap-3 rounded-xl border-2 p-3 text-left transition-colors ${selected ? "border-hz-cyan bg-hz-cyan/5" : full ? "cursor-not-allowed border-hz-gray/40 bg-hz-gray/10 opacity-60" : "border-hz-gray/50 bg-white hover:border-hz-cyan/50"}`}>
                                <div className="min-w-0">
                                  <p className="text-sm font-bold text-hz-ink">{w.name}</p>
                                  {w.speaker_name && <p className="mt-0.5 text-xs text-hz-ink/60">{w.speaker_name}</p>}
                                  {w.schedule_text && (
                                    <p className="mt-0.5 text-xs font-medium capitalize text-hz-ink/70" data-testid={`ws-schedule-${i}-${w.id}`}>
                                      {w.schedule_text}{w.duration_minutes ? ` · ${w.duration_minutes} min` : ""}{w.room_name ? ` · ${w.room_name}` : ""}
                                    </p>
                                  )}
                                  {w.description && <p className="mt-0.5 line-clamp-2 text-xs text-hz-ink/60">{w.description}</p>}
                                  <p className="mt-0.5 text-xs font-semibold text-hz-blue">{full ? "Cupo lleno" : `${w.available} de ${w.capacity} lugares`}</p>
                                  {conflict && (
                                    <p className="mt-1 text-xs font-semibold text-hz-warning" data-testid={`ws-conflict-note-${i}-${w.id}`}>Horario en conflicto con un taller seleccionado</p>
                                  )}
                                </div>
                                <span className={`flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${selected ? "bg-hz-cyan text-white" : "bg-hz-gray/30 text-hz-navy"}`}>
                                  {selected && <><Check className="h-3.5 w-3.5" />Seleccionado</>}
                                </span>
                              </button>
                            );
                          })}
                          {swapPrompt && swapPrompt.adultIdx === i && (
                            <div className="rounded-lg border border-hz-warning/50 bg-hz-warning/5 px-4 py-3" data-testid="ws-conflict-prompt">
                              <p className="text-xs font-semibold text-hz-ink">
                                No puedes seleccionar este taller porque coincide con "{swapPrompt.conflictName}", programado de {swapPrompt.conflictTime}.
                              </p>
                              <div className="mt-2 flex flex-wrap gap-2">
                                <Button size="sm" variant="outline" onClick={() => resolveSwap(false)} data-testid="ws-conflict-keep" className="font-bold">Conservar taller actual</Button>
                                <Button size="sm" onClick={() => resolveSwap(true)} data-testid="ws-conflict-replace" className="bg-hz-warning font-bold text-white hover:bg-hz-warning/90">Quitar "{swapPrompt.conflictName}" y seleccionar el nuevo</Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {regType !== "individual" && (
                  <Button type="button" variant="outline" onClick={() => setAdults((l) => [...l, newAdult()])} data-testid="add-adult-btn" className="w-full border-dashed font-bold text-hz-navy">
                    <Plus className="mr-2 h-4 w-4" /> Agregar adulto
                  </Button>
                )}
              </div>
            )}

            {/* PASO 3: niños */}
            {step === 3 && regType !== "group" && (
              <div className="space-y-6" data-testid="step-children">
                <div>
                  <h2 className="flex items-center gap-2 text-lg font-bold text-hz-navy"><Baby className="h-5 w-5 text-hz-cyan" /> Niños acompañantes (cuidado infantil)</h2>
                  <p className="mt-1 text-xs text-hz-ink/60">Opcional. Cada menor requiere fotografía y al menos un adulto autorizado para recogerlo.</p>
                </div>
                {children.map((c, i) => (
                  <div key={i} className="space-y-4 rounded-xl border border-hz-gray/40 bg-hz-bg/50 p-4" data-testid={`child-card-${i}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-bold text-hz-navy">Niño {i + 1}</p>
                      <button type="button" onClick={() => removeChild(i)} data-testid={`child-remove-${i}`} className="text-xs font-bold text-hz-error hover:underline">
                        <Trash2 className="mr-1 inline h-3.5 w-3.5" /> Eliminar
                      </button>
                    </div>
                    <div>
                      <Label className="font-semibold text-hz-ink">Nombre completo *</Label>
                      <Input data-testid={`child-name-${i}`} value={c.full_name} onChange={(e) => updateChild(i, { full_name: e.target.value })} className="mt-1.5" />
                      <FieldError msg={errors[`child_name_${i}`]} id={`child-name-${i}`} />
                    </div>
                    <div>
                      <Label className="font-semibold text-hz-ink">Fotografía del menor (puedes completarla después)</Label>
                      <PhotoPicker preview={c.photoPreview} testId={`child-photo-${i}`}
                        onPick={(f) => pickPhoto("child", i, f)}
                        onRemove={() => updateChild(i, { photoFile: null, photoPreview: null })} />
                      <p className="mt-1 text-xs font-medium text-hz-blue">La fotografía se usará exclusivamente para identificación y entrega segura en el área de cuidado infantil.</p>
                      <FieldError msg={errors[`photo_child_${i}`]} id={`child-photo-${i}`} />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <Label className="text-xs font-semibold text-hz-ink">Fecha de nacimiento</Label>
                        <Input type="date" data-testid={`child-birthdate-${i}`} value={c.birth_date} onChange={(e) => updateChild(i, { birth_date: e.target.value })} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-hz-ink">Edad</Label>
                        <Input type="number" min="0" max="17" data-testid={`child-age-${i}`} value={c.age} onChange={(e) => updateChild(i, { age: e.target.value })} className="mt-1" />
                      </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div>
                        <Label className="text-xs font-semibold text-hz-ink">Alergias</Label>
                        <Input data-testid={`child-allergies-${i}`} value={c.allergies} onChange={(e) => updateChild(i, { allergies: e.target.value })} className="mt-1" placeholder="Ej. Nueces" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-hz-ink">Notas médicas</Label>
                        <Input data-testid={`child-medical-${i}`} value={c.medical_notes} onChange={(e) => updateChild(i, { medical_notes: e.target.value })} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-hz-ink">Necesidades especiales</Label>
                        <Input data-testid={`child-special-${i}`} value={c.special_needs} onChange={(e) => updateChild(i, { special_needs: e.target.value })} className="mt-1" />
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm font-medium text-hz-ink">
                      <Checkbox data-testid={`child-care-${i}`} checked={c.care_required} onCheckedChange={(v) => updateChild(i, { care_required: v === true })} />
                      Requerirá acceso al área de cuidado infantil
                    </label>
                    {c.care_required && pricing.child_care_fee?.requirements_message && (
                      <p className="rounded-lg bg-hz-bg px-3 py-2 text-xs font-medium text-hz-ink/70" data-testid={`child-care-requirements-${i}`}>{pricing.child_care_fee.requirements_message}</p>
                    )}
                    <div>
                      <Label className="text-xs font-semibold text-hz-ink">Adultos del registro autorizados para recogerlo *</Label>
                      <div className="mt-2 space-y-2">
                        {adults.map((a, ai) => (
                          <label key={ai} className="flex items-center gap-2 text-sm font-medium text-hz-ink">
                            <Checkbox data-testid={`child-auth-${i}-${ai}`} checked={c.authorized.includes(ai)}
                              onCheckedChange={(v) => updateChild(i, { authorized: v ? [...c.authorized, ai] : c.authorized.filter((x) => x !== ai) })} />
                            {(regType === "individual" ? contact.name : a.full_name) || `Adulto ${ai + 1}`}
                          </label>
                        ))}
                      </div>
                      <FieldError msg={errors[`child_auth_${i}`]} id={`child-auth-${i}`} />
                    </div>
                    <div className="rounded-lg border border-hz-gray/40 bg-white p-3">
                      <p className="text-xs font-bold text-hz-navy">Tutor externo autorizado (opcional)</p>
                      <div className="mt-2 grid gap-2 sm:grid-cols-3">
                        <Input placeholder="Nombre" data-testid={`child-ext-name-${i}`} value={c.extName} onChange={(e) => updateChild(i, { extName: e.target.value })} />
                        <Input placeholder="Teléfono" data-testid={`child-ext-phone-${i}`} value={c.extPhone} onChange={(e) => updateChild(i, { extPhone: e.target.value })} />
                        <Input placeholder="Relación (ej. abuela)" data-testid={`child-ext-rel-${i}`} value={c.extRelationship} onChange={(e) => updateChild(i, { extRelationship: e.target.value })} />
                      </div>
                    </div>
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={() => setChildren((l) => [...l, newChild()])} data-testid="add-child-btn" className="w-full border-dashed font-bold text-hz-navy">
                  <Plus className="mr-2 h-4 w-4" /> Agregar niño
                </Button>
              </div>
            )}

            {/* PASO FINAL: resumen */}
            {step === maxStep && (
              <div className="space-y-6" data-testid="step-summary">
                <h2 className="text-lg font-bold text-hz-navy">Resumen y confirmación</h2>
                <div className="rounded-xl border border-hz-gray/40 bg-hz-bg p-4 text-sm" data-testid="summary-contact">
                  <p className="font-bold text-hz-navy">{contact.groupName || "Registro"} · {REG_TYPES.find((t) => t.id === regType)?.title}</p>
                  <p className="text-hz-ink/70">Contacto: {contact.name}{contact.email ? ` · ${contact.email}` : ""}{contact.phone ? ` · ${contact.phone}` : ""}</p>
                  <p className="mt-1 font-semibold text-hz-navy">{adults.length} adulto(s) · {children.length} niño(s)</p>
                </div>

                {adults.map((a, i) => (
                  <div key={i} className="rounded-xl border border-hz-gray/40" data-testid={`summary-adult-${i}`}>
                    <div className="flex items-center gap-3 border-b border-hz-gray/40 px-4 py-3">
                      {a.photoPreview && <img src={a.photoPreview} alt="" className="h-10 w-10 rounded-lg object-cover" />}
                      <p className="text-sm font-bold text-hz-navy">{(regType === "individual" ? contact.name : a.full_name) || `Adulto ${i + 1}`}</p>
                    </div>
                    <div className="divide-y divide-hz-gray/30 text-sm">
                      {a.general_event && (
                        <div className="flex items-center justify-between px-4 py-2.5">
                          <span className="text-hz-ink">{pricing.general_event?.name || "Evento general"}</span>
                          <span className="text-xs font-bold text-hz-cyan">Seleccionado</span>
                        </div>
                      )}
                      {a.dinner && (
                        <div className="flex items-center justify-between px-4 py-2.5">
                          <span className="text-hz-ink">Cena especial — viernes</span>
                          <span className={`text-xs font-bold ${dinnerWaitlistMode ? "text-hz-warning" : "text-hz-cyan"}`} data-testid={`summary-dinner-${i}`}>
                            {dinnerWaitlistMode ? "En lista de espera (sujeto a disponibilidad)" : "Solicitada"}
                          </span>
                        </div>
                      )}
                      {a.workshops.map((wid) => {
                        const w = workshops.find((x) => x.id === wid);
                        return w ? (
                          <div key={wid} className="flex items-center justify-between px-4 py-2.5">
                            <span className="text-hz-ink">Taller: {w.name}{w.schedule_text ? ` · ${w.schedule_text}` : ""}</span>
                            <span className="text-xs font-bold text-hz-cyan">Seleccionado</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                ))}

                {children.length > 0 && (
                  <div className="rounded-xl border border-hz-gray/40" data-testid="summary-children">
                    <p className="border-b border-hz-gray/40 px-4 py-3 text-sm font-bold text-hz-navy">Niños ({children.length})</p>
                    {children.map((c, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <span className="text-hz-ink">{c.full_name || `Niño ${i + 1}`}{c.care_required ? " · cuidado infantil" : ""}</span>
                        <span className="text-xs font-bold text-hz-cyan" data-testid={`summary-child-fee-${i}`}>
                          {c.care_required ? "Cuidado infantil solicitado" : "Sin cuidado infantil"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {regType !== "individual" && (
                  <p className="rounded-xl border border-hz-cyan/30 bg-hz-cyan/5 px-4 py-3 text-sm font-medium text-hz-ink" data-testid="summary-single-payment-notice">
                    El pago se realiza mediante un solo comprobante por el total del grupo. Si algún integrante pagará por separado, debe registrarse en otro grupo.
                  </p>
                )}
                <div className="rounded-xl bg-hz-navy/5 px-4 py-3" data-testid="summary-total-box">
                  {quoteLoading ? (
                    <p className="text-sm font-bold text-hz-navy" data-testid="summary-total">Actualizando total…</p>
                  ) : quoteValid ? (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-hz-navy">{quote.total_label || "Total a pagar"}</span>
                      <span className="text-base font-extrabold text-hz-navy" data-testid="summary-total">{money(quote.total_amount)} MXN</span>
                    </div>
                  ) : quoteFailed ? (
                    <div className="flex flex-wrap items-center justify-between gap-2" data-testid="summary-total-error">
                      <p className="text-sm font-semibold text-hz-ink">{(quote && (quote.errors || [])[0]) || "No fue posible calcular el total en este momento. Intenta nuevamente."}</p>
                      <Button type="button" variant="outline" size="sm" onClick={() => setQuoteRetry((r) => r + 1)} data-testid="summary-total-retry" className="font-bold">Reintentar</Button>
                    </div>
                  ) : (
                    <p className="text-sm font-semibold text-hz-ink" data-testid="summary-total-hint">Completa tus selecciones para consultar el total de tu registro.</p>
                  )}
                </div>
                {quoteValid && quote?.total_note && <p className="text-xs leading-relaxed text-hz-ink/60" data-testid="summary-total-note">{quote.total_note}</p>}

                <div className="flex items-start gap-3">
                  <Checkbox id="privacy" data-testid="privacy-checkbox" checked={privacy} onCheckedChange={(v) => setPrivacy(v === true)} className="mt-0.5" />
                  <Label htmlFor="privacy" className="text-sm font-medium leading-relaxed text-hz-ink">
                    Confirmo que los datos proporcionados son correctos y acepto el aviso de privacidad, incluido el tratamiento de fotografías de adultos para identificación y control de acceso.
                  </Label>
                </div>
                <FieldError msg={errors.privacy} id="privacy" />
                {children.length > 0 && (
                  <>
                    <div className="flex items-start gap-3">
                      <Checkbox id="minors" data-testid="minors-auth-checkbox" checked={minorsAuth} onCheckedChange={(v) => setMinorsAuth(v === true)} className="mt-0.5" />
                      <Label htmlFor="minors" className="text-sm font-medium leading-relaxed text-hz-ink">
                        Confirmo que soy padre, madre o tutor, o que cuento con la autorización de los padres o tutores para registrar a los menores, incluidas sus fotografías y datos de salud, los cuales se usarán únicamente para su cuidado y entrega segura durante el evento.
                      </Label>
                    </div>
                    <FieldError msg={errors.minors} id="minors" />
                  </>
                )}

                {duplicateWarn && (
                  <div className="flex items-start gap-3 rounded-xl border border-hz-warning/40 bg-hz-warning/5 p-4" data-testid="duplicate-warning" role="alert">
                    <AlertTriangle className="h-5 w-5 shrink-0 text-hz-warning" />
                    <div className="text-sm text-hz-ink">
                      <p className="font-semibold">{duplicateWarn}</p>
                      <Button type="button" variant="outline" size="sm" onClick={() => submit(true)} disabled={submitting} data-testid="duplicate-confirm-btn" className="mt-2 font-bold">
                        Es una persona diferente, continuar
                      </Button>
                    </div>
                  </div>
                )}
                {submitError && (
                  <div className="flex items-start gap-3 rounded-xl border border-hz-error/30 bg-hz-error/5 p-4" data-testid="submit-error" role="alert">
                    <AlertTriangle className="h-5 w-5 shrink-0 text-hz-error" />
                    <p className="text-sm font-semibold text-hz-ink">{submitError}</p>
                  </div>
                )}
              </div>
            )}

            <div className="mt-8 flex items-center justify-between">
              {step > 1 ? (
                <Button type="button" variant="outline" onClick={back} disabled={submitting} data-testid="step-back-btn" className="font-bold">
                  <ArrowLeft className="mr-1 h-4 w-4" /> Anterior
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={() => setRegType(null)} disabled={submitting} data-testid="change-type-btn" className="font-bold">
                  <ArrowLeft className="mr-1 h-4 w-4" /> Tipo de registro
                </Button>
              )}
              {step < maxStep ? (
                <Button type="button" onClick={next} data-testid="step-next-btn" className="bg-hz-navy font-bold text-white hover:bg-hz-blue">
                  Siguiente <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <Button type="button" onClick={() => submit(false)} disabled={submitting || !privacy || !quoteValid} data-testid="submit-registration-btn" className="bg-hz-cyan font-bold text-white hover:bg-hz-blue disabled:opacity-60">
                  {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enviando…</>) : "Enviar pre-registro"}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </PublicLayout>
  );
};

export default RegistroPage;
